var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { createSelector } from "@reduxjs/toolkit";
import { isEqual } from "lodash";
import { TubeType, ChecklistItemName, NodeComponentType, tubeConnectionSelectionTypes, edgeSelectionTypes, SelectionDecentralizedPumpType, } from "store/types";
import { objectShapes, objectPosition, suctionCupModels, toolChangerDefaultLength, tubeConnectionModels, } from "3d/constants/common";
import { isBoxCentreOnPallet } from "3d/pallet";
import { findCupTargetBox } from "3d/cup-box-assignment";
import { cupGridPitch } from "3d/cup-grid-fill";
import { LAYERS } from "store/layers";
import { createPalletLayout } from "store/scene/pallet-layout";
import { countAvailableSelectionIds } from "store/system-data/helpers";
import { getCylinderVolumeDm3 } from "3d/helpers";
import * as uiSelectors from "store/ui/selectors";
import * as sceneSelectors from "store/scene/selectors";
import * as systemDataSelectorsDefineParameters from "store/system-data/define-parameters/selectors";
import * as systemDataSelectorsSelectApplication from "store/system-data/select-application/selectors";
import * as systemPerformanceSelectors from "store/system-performance/selectors";
import { mergeBoxLiftingForcesGraphData, } from "store/system-performance/graph-data-transforms";
import * as userSelectors from "store/user/selectors";
import * as undoSnapshotsSelectors from "store/undo-snapshots/selectors";
import { metalObjectShapes, suctionCupModelsMetal } from "3d/constants/metal";
import { glassObjectShapes, suctionCupModelsGlass } from "3d/constants/glass";
import { plasticObjectShapes, suctionCupModelsPlastic, } from "3d/constants/plastic";
import { TactonApplication } from "api/tacton/types";
import { systemDataConstants } from "api/system-data";
import { CameraViewId, } from "3d/types";
import { cardboardObjectShapes, suctionCupModelsCardboard, } from "3d/constants/cardboard";
import { isObjectShapeAvailable } from "utility/feature-availability";
import { summarizeFlowRestrictions } from "utility/flow-restrictions";
var selectSelectedNode = createSelector(uiSelectors.selectSelectedId, sceneSelectors.selectNodes, function (selectedId, nodes) { return (selectedId ? nodes[selectedId] : undefined); });
var selectFilteredSelectedNodes = createSelector(uiSelectors.selectSelectedIds, sceneSelectors.selectNodes, uiSelectors.selectSelectionFilter, systemDataSelectorsDefineParameters.selectPumpSelectionDecentralMap, systemDataSelectorsDefineParameters.selectSystemDataDefineParameters, function (selectedIds, nodes, selectionFilter, pumpSelectionDecentralMap, systemData) {
    var hasPump = function (nodeId) {
        var selectionId = pumpSelectionDecentralMap[nodeId];
        return systemData === null || systemData === void 0 ? void 0 : systemData.vacuumPumpSelectionsDecentralized[selectionId];
    };
    return selectedIds
        .map(function (id) { return nodes[id]; })
        .filter(Boolean)
        .filter(function (node) {
        return selectionFilter === undefined ||
            node.type === selectionFilter ||
            (node.type === NodeComponentType.TUBE_CONNECTION &&
                tubeConnectionSelectionTypes[node.component.tubeType] === selectionFilter) ||
            (selectionFilter === SelectionDecentralizedPumpType &&
                node.type === NodeComponentType.GRIP_POSITION &&
                hasPump(node.id));
    });
});
var selectSelectedNodes = createSelector(uiSelectors.selectSelectedIds, sceneSelectors.selectNodes, function (selectedIds, nodes) { return selectedIds.map(function (id) { return nodes[id]; }).filter(Boolean); });
var selectSelectedNodesType = createSelector(selectSelectedNodes, function (selectedNodes) {
    var _a;
    var selectedNodesType = selectedNodes.every(function (node) { var _a; return (node === null || node === void 0 ? void 0 : node.type) === ((_a = selectedNodes[0]) === null || _a === void 0 ? void 0 : _a.type); })
        ? (_a = selectedNodes[0]) === null || _a === void 0 ? void 0 : _a.type
        : null;
    return selectedNodesType;
});
var selectFilteredSelectedNodesType = createSelector(selectFilteredSelectedNodes, function (selectedNodes) {
    var _a;
    var selectedNodesType = selectedNodes.every(function (node) { var _a; return (node === null || node === void 0 ? void 0 : node.type) === ((_a = selectedNodes[0]) === null || _a === void 0 ? void 0 : _a.type); })
        ? (_a = selectedNodes[0]) === null || _a === void 0 ? void 0 : _a.type
        : null;
    return selectedNodesType;
});
var selectSelectedAttachment = createSelector(uiSelectors.selectSelectedId, sceneSelectors.selectAttachments, function (selectedId, attachments) {
    return selectedId ? attachments[selectedId] : undefined;
});
var selectSelectedEdge = createSelector(uiSelectors.selectSelectedId, sceneSelectors.selectEdges, function (selectedId, edges) { return (selectedId ? edges[selectedId] : undefined); });
var selectSelectedEdges = createSelector(uiSelectors.selectSelectedIds, sceneSelectors.selectEdges, uiSelectors.selectSelectionFilter, function (selectedIds, edges, selectionFilter) {
    return selectedIds
        .map(function (id) { return edges[id]; })
        .filter(function (edge) {
        return edge !== undefined &&
            (selectionFilter === undefined ||
                edgeSelectionTypes[edge.tubeType] === selectionFilter);
    });
});
var selectCanAddGripPosition = createSelector(sceneSelectors.selectHasAvailableIdsForGripPosition, systemDataSelectorsDefineParameters.selectHasAvailableSelectionsForGripPosition, function (hasIds, hasSelections) { return hasIds && hasSelections; });
var selectCanAddCentralizedPump = createSelector(systemDataSelectorsDefineParameters.selectIsCentralizedSystem, sceneSelectors.selectHasAvailableIdsForPump, systemDataSelectorsDefineParameters.selectHasAvailableSelectionsForPump, sceneSelectors.allowAddCentralizedPump, function (isCentralizedSystem, hasIds, hasSelections, allowAddCentralizedPump) {
    return isCentralizedSystem && hasIds && hasSelections && allowAddCentralizedPump;
});
/* The scene already holds as many central pumps as it can: the pump count cap
 * or every Tacton pump slot is taken. The toolbar greys "Add pump" out then
 * rather than hiding it. */
var selectIsCentralizedPumpLimitReached = createSelector(systemDataSelectorsDefineParameters.selectIsCentralizedSystem, systemDataSelectorsDefineParameters.selectHasAvailableSelectionsForPump, sceneSelectors.allowAddCentralizedPump, function (isCentralizedSystem, hasSelections, allowAddCentralizedPump) {
    return Boolean(isCentralizedSystem) &&
        (!hasSelections || !allowAddCentralizedPump);
});
/*
 * Central pump nodes that have no pump committed to Tacton: the placeholders
 * "Add pump" puts in the scene for the user to pick a pump for. A placeholder
 * is nothing but such a node — it is drawn as a plain block, has no tube
 * attachments and is left out of the calculation until a pump is picked.
 */
var selectCentralPumpNodesWithoutPump = createSelector(sceneSelectors.selectCentralizedPumps, systemDataSelectorsDefineParameters.selectSystemDataDefineParameters, systemDataSelectorsDefineParameters.selectPumpSelectionCentralMap, function (pumpNodes, systemData, pumpSelectionCentralMap) {
    return pumpNodes.filter(function (node) {
        var selectionId = pumpSelectionCentralMap[node.id];
        var value = selectionId && (systemData === null || systemData === void 0 ? void 0 : systemData.vacuumPumpSelectionsCentralized[selectionId]);
        return !value || !(systemData === null || systemData === void 0 ? void 0 : systemData.vacuumPumpComponents[value]);
    });
});
var selectCanAddTubeConnection = createSelector(sceneSelectors.selectHasAvailableIdsForTubeConnection, function (hasIds) { return hasIds; });
/*
 * How many more grip positions can be added right now. Every grip position
 * needs a free Tacton selection slot for its cup, mounting and decentral pump
 * (and cup technology where the application has any), plus its share of the
 * node id budget — the tightest of those is the answer. What the fill tool
 * shows as "can still be added" and refuses to exceed.
 */
var selectAvailableGripPositionCount = createSelector(systemDataSelectorsDefineParameters.selectSystemDataDefineParameters, systemDataSelectorsDefineParameters.selectCupSelectionMap, systemDataSelectorsDefineParameters.selectCupMountingSelectionMap, systemDataSelectorsDefineParameters.selectPumpSelectionDecentralMap, systemDataSelectorsDefineParameters.selectCupTechnologySelectionMap, sceneSelectors.selectAvailableGripPositionIdCount, function (systemData, cupSelectionMap, cupMountingSelectionMap, pumpSelectionDecentralMap, cupTechnologySelectionMap, availableIdCount) {
    if (!systemData)
        return 0;
    var counts = [
        availableIdCount,
        countAvailableSelectionIds(systemData.suctionCupSelections, cupSelectionMap),
        countAvailableSelectionIds(systemData.cupMountingSelections, cupMountingSelectionMap),
        countAvailableSelectionIds(systemData.vacuumPumpSelectionsDecentralized, pumpSelectionDecentralMap),
    ];
    if (Object.keys(systemData.cupTechnologySelections).length > 0) {
        counts.push(countAvailableSelectionIds(systemData.cupTechnologySelections, cupTechnologySelectionMap));
    }
    return Math.min.apply(Math, __spreadArray([], __read(counts), false));
});
/*
 * Everything the fill tool needs while armed — null when it is off, in the 3D
 * view (the tool previews on the flat 2D view only), or when its anchor is
 * gone (deleted, undone): the tool has no control of its own in the scene, so
 * it must never appear to be on without the cup it is about.
 */
var selectCupFillLayout = createSelector(uiSelectors.selectCupFill, uiSelectors.selectCameraViewId, sceneSelectors.selectNodes, systemDataSelectorsDefineParameters.selectCupComponentsByNode, selectAvailableGripPositionCount, function (cupFill, cameraViewId, nodes, cupsByNode, limit) {
    if (!cupFill || cameraViewId === CameraViewId.INITIAL_3D_VIEW)
        return null;
    var anchor = nodes[cupFill.anchorNodeId];
    if (!anchor || anchor.type !== NodeComponentType.GRIP_POSITION)
        return null;
    /* The selected cup's real footprint; the marker's size stands in for a
     * grip position that has no cup yet. */
    var cupComponent = cupsByNode[anchor.id];
    var marker = anchor.component;
    var cup = {
        center: { x: anchor.position.x, z: anchor.position.z },
        width: (cupComponent === null || cupComponent === void 0 ? void 0 : cupComponent.width) || marker.width,
        depth: (cupComponent === null || cupComponent === void 0 ? void 0 : cupComponent.height) || marker.height || marker.width,
        rotationY: anchor.rotationY,
    };
    return {
        anchorNodeId: anchor.id,
        anchor: cup.center,
        planeY: anchor.position.y,
        cup: cup,
        gap: cupFill.gap,
        pitch: cupGridPitch(cup, cupFill.gap),
        limit: limit,
    };
});
/**
 * Volume (in dm³ / litres) contributed by the tubing in the system: the vacuum
 * edges, the vacuum tool-changer tube connections and — for decentral inline
 * pumps — the inline tubes. Kept separate from the component volume so the
 * component data panel can show how much of the total comes from tubing.
 */
var selectVacuumSystemTubingVolume = createSelector(systemDataSelectorsDefineParameters.selectIsCentralizedSystem, systemDataSelectorsDefineParameters.selectSystemDataDefineParameters, systemDataSelectorsDefineParameters.selectPumpSelectionDecentralMap, sceneSelectors.selectEdges, sceneSelectors.selectTubeConnections, sceneSelectors.selectGripPositions, function (isCentralizedSystem, systemData, pumpSelectionDecentralMap, edges, tubeConnections, gripPositions) {
    if (!systemData)
        return 0;
    var edgeVolume = Object.values(edges || {}).reduce(function (volume, edge) {
        if (edge.tubeType === TubeType.VACUUM) {
            return (volume + getCylinderVolumeDm3(edge.length, edge.diameterDefaultTube));
        }
        else {
            return volume;
        }
    }, 0);
    var toolChangerVolume = tubeConnections.reduce(function (volume, tubeConnection) {
        var component = tubeConnection.component;
        if (component.type !== undefined &&
            tubeConnectionModels[component.type].type === "tool-changer" &&
            component.tubeType === TubeType.VACUUM) {
            return (volume +
                getCylinderVolumeDm3(toolChangerDefaultLength, component.diameters[0]));
        }
        else {
            return volume;
        }
    }, 0);
    var inlineTubesVolume = isCentralizedSystem
        ? 0
        : gripPositions.reduce(function (volume, gripPosition) {
            var _a;
            var selectionId = pumpSelectionDecentralMap[gripPosition.id];
            if (!selectionId)
                return volume;
            var pumpValue = systemData.vacuumPumpSelectionsDecentralized[selectionId];
            if (!pumpValue)
                return volume;
            var isInline = (_a = systemData.vacuumPumpComponents[pumpValue]) === null || _a === void 0 ? void 0 : _a.isInlineDecentralPump;
            if (isInline) {
                var component = gripPosition.component;
                return (volume +
                    getCylinderVolumeDm3(component.inlineTubeLength, component.inlineTubeDiameter));
            }
            else {
                return volume;
            }
        }, 0);
    return edgeVolume + toolChangerVolume + inlineTubesVolume;
});
/**
 * Volume (in dm³ / litres) contributed by the catalog components (cups,
 * mountings, technologies, pumps), as calculated by Tacton.
 */
var selectVacuumSystemComponentVolume = createSelector(systemDataSelectorsDefineParameters.selectSystemDataDefineParameters, function (systemData) { var _a; return (_a = systemData === null || systemData === void 0 ? void 0 : systemData.output.totalVolume) !== null && _a !== void 0 ? _a : 0; });
var selectVacuumSystemVolume = createSelector(selectVacuumSystemTubingVolume, selectVacuumSystemComponentVolume, function (tubingVolume, componentVolume) { return tubingVolume + componentVolume; });
/* The calculated sense valves/restrictors, labelled with their Tacton codes */
var selectFlowRestrictionSummary = createSelector(systemPerformanceSelectors.selectShownOrWithheldPerformanceData, systemDataSelectorsDefineParameters.selectSystemDataDefineParameters, systemDataSelectorsDefineParameters.selectCupTechnologySelectionMap, systemPerformanceSelectors.selectFlowRestrictionFindingSeverities, function (performanceData, systemData, cupTechnologySelectionMap, severities) {
    if (!performanceData || !systemData)
        return;
    return summarizeFlowRestrictions(Object.entries(performanceData.nodeData).flatMap(function (_a) {
        var _b = __read(_a, 2), nodeId = _b[0], flowRestriction = _b[1].flowRestriction;
        if (!flowRestriction)
            return [];
        var cupTechnology = systemDataSelectorsDefineParameters.getSelectedCupTechnology(systemData, cupTechnologySelectionMap, nodeId);
        return [
            {
                code: (cupTechnology === null || cupTechnology === void 0 ? void 0 : cupTechnology.code) || (cupTechnology === null || cupTechnology === void 0 ? void 0 : cupTechnology.label) || "",
                performance: flowRestriction,
                findingSeverity: severities[nodeId],
            },
        ];
    }));
});
var selectIsSystemDataLoading = createSelector(systemDataSelectorsDefineParameters.selectIsLoadingDefineParameters, systemDataSelectorsSelectApplication.selectIsLoadingSelectApplication, function (isLoadingDefineParameters, isLoadingSelectApplication) {
    return isLoadingDefineParameters || isLoadingSelectApplication;
});
export var selectUseTexturedSurfaces = createSelector(systemDataSelectorsDefineParameters.selectSystemDataDefineParameters, function (systemData) {
    var _a;
    var texturedSurfaceValue = (_a = systemData === null || systemData === void 0 ? void 0 : systemData.parameters[systemDataConstants.parameterNames.texturedSurface]) === null || _a === void 0 ? void 0 : _a.value;
    // Currently, if the active application is any other than Plastic, this value will be undefined.
    // We could just check if the active application is Plastic, but in the future this value might be exposed in the General application.
    // So I thought it would be best to prepare for that.
    var texturedSurfaceValueExists = texturedSurfaceValue !== undefined;
    if (!texturedSurfaceValueExists)
        return false;
    return (texturedSurfaceValue ===
        systemDataConstants.parameterValues.texturedSurfaceOn);
});
var selectSystemDataErrors = createSelector(systemDataSelectorsDefineParameters.selectErrorsDefineParameters, systemDataSelectorsSelectApplication.selectErrorsSelectApplication, function (errorsDefineParameters, errorsSelectApplication) {
    var errors = [];
    if (!errorsDefineParameters && !errorsSelectApplication)
        return;
    errorsDefineParameters && errors.push.apply(errors, __spreadArray([], __read(errorsDefineParameters), false));
    errorsSelectApplication && errors.push.apply(errors, __spreadArray([], __read(errorsSelectApplication), false));
    return errors;
});
export var selectObjectShapes = createSelector(systemDataSelectorsSelectApplication.selectActiveApplication, selectUseTexturedSurfaces, function (activeApplication, useTexturedSurfaces) {
    var filterObjectShapes = function (objectShapes, filterFn) {
        return Object.entries(objectShapes)
            .filter(function (_a) {
            var _b = __read(_a, 2), _key = _b[0], objectShape = _b[1];
            return filterFn(objectShape);
        })
            .reduce(function (acc, _a) {
            var _b = __read(_a, 2), key = _b[0], objectShape = _b[1];
            acc[key] = objectShape;
            return acc;
        }, {});
    };
    var filterByTexturedSurfaceCompability = function (objectShape) {
        return useTexturedSurfaces ? !!objectShape.texturedMaterialTransforms : true;
    };
    switch (activeApplication) {
        case TactonApplication.SHEET_METAL:
            return filterObjectShapes(metalObjectShapes, isObjectShapeAvailable);
        case TactonApplication.GLASS:
            return glassObjectShapes;
        case TactonApplication.PLASTIC:
            return filterObjectShapes(plasticObjectShapes, filterByTexturedSurfaceCompability);
        case TactonApplication.CARDBOARD:
            return cardboardObjectShapes;
        default:
            return filterObjectShapes(objectShapes, filterByTexturedSurfaceCompability);
    }
});
export var selectSuctionCupModels = createSelector(systemDataSelectorsSelectApplication.selectActiveApplication, function (activeApplication) {
    switch (activeApplication) {
        case TactonApplication.SHEET_METAL:
            return suctionCupModelsMetal;
        case TactonApplication.GLASS:
            return suctionCupModelsGlass;
        case TactonApplication.PLASTIC:
            return suctionCupModelsPlastic;
        case TactonApplication.CARDBOARD:
            return suctionCupModelsCardboard;
        default:
            return suctionCupModels;
    }
});
export var selectCalculatePerformanceEnabled = createSelector(systemPerformanceSelectors.selectIsPerformanceLoading, function (isSystemPerformanceLoading) {
    return !isSystemPerformanceLoading;
});
/**
 * Structural validation of the system (grip positions added, no empty grip
 * positions, parts connected). This is everything we can know without having
 * run a calculation, so it's safe to use as a gate for starting one — unlike
 * the sanity check, which only comes back inside the calculation response.
 */
export var selectCalculatePerformanceStructuralErrorMessage = createSelector(uiSelectors.selectChecklist, sceneSelectors.selectHasGripPositionsWithMissingComponents, systemDataSelectorsDefineParameters.selectIsCentralizedSystem, function (checklist, hasGripPositionsWithMissingComponents, isCentralizedSystem) {
    if (!checklist[ChecklistItemName.ADD_GRIP_POSITION].completed) {
        return "notifications.no_grip_positions";
    }
    if (hasGripPositionsWithMissingComponents) {
        return "notifications.empty_grip_positions".concat(isCentralizedSystem ? "" : "_decentralized");
    }
    if (!checklist[ChecklistItemName.CONNECT_PARTS].completed) {
        return "notifications.unconnected_parts";
    }
    return null;
});
export var selectCalculatePerformanceValidationErrorMessage = createSelector(selectCalculatePerformanceStructuralErrorMessage, systemPerformanceSelectors.selectSanityCheckData, function (structuralErrorMessage, sanityCheckData) {
    if (structuralErrorMessage) {
        return structuralErrorMessage;
    }
    if (!(sanityCheckData === null || sanityCheckData === void 0 ? void 0 : sanityCheckData.validationPassed)) {
        return "notifications.sanity_check_failed";
    }
    return null;
});
/*
 * The footprint of every suction cup in the scene: the grip position node's
 * X/Z and rotation joined with the selected cup component's width/depth.
 * Grip positions with no cup selected are absent — a cup that doesn't exist
 * can't sit on (or miss) a box, matching the old raycast which only ran once a
 * cup was selected.
 */
var selectCupFootprints = createSelector(sceneSelectors.selectNodes, systemDataSelectorsDefineParameters.selectCupComponentsByNode, function (nodes, cupsByNode) {
    var footprints = [];
    Object.values(nodes).forEach(function (node) {
        if (node.type !== NodeComponentType.GRIP_POSITION)
            return;
        var cup = cupsByNode[node.id];
        if (!(cup === null || cup === void 0 ? void 0 : cup.width) || !(cup === null || cup === void 0 ? void 0 : cup.height))
            return;
        footprints.push({
            nodeId: node.id,
            center: { x: node.position.x, z: node.position.z },
            width: cup.width,
            depth: cup.height,
            rotationY: node.rotationY,
        });
    });
    return footprints;
});
var emptyCupBoxHits = function () {
    return LAYERS.reduce(function (hits, layer) {
        hits[layer] = { assignments: {}, outside: [] };
        return hits;
    }, {});
};
/*
 * Which box each cup sits over, per stacking layer — derived from scene
 * geometry, never stored. Boxes are axis-aligned rectangles and cup footprints
 * are ellipses, all described completely by store state, so this is pure maths
 * (see `3d/cup-box-assignment`); it updates in the same dispatch as the drag
 * that moved a cup or a box, with no polling latency.
 *
 * Only the cardboard application has boxes; everywhere else this is empty and
 * the shaped-object raycast (`isCupAboveSolidSurface`) provides the
 * cup-off-object signal instead — see `selectCupsOutsideOfObjectByLayer`.
 *
 * The result is compared by value so its identity only changes when an
 * assignment actually did — consumers (and effects keyed on it) don't churn on
 * every unrelated node edit.
 */
var selectCupBoxHitsByLayer = createSelector(selectCupFootprints, sceneSelectors.selectCardboardBoxes, sceneSelectors.selectObjectSize, systemDataSelectorsSelectApplication.selectActiveApplication, function (footprints, cardboardBoxes, objectSize, activeApplication) {
    var hits = emptyCupBoxHits();
    // Defensive: partially loaded / legacy persisted states may lack these.
    if (activeApplication !== TactonApplication.CARDBOARD ||
        !objectSize ||
        !cardboardBoxes) {
        return hits;
    }
    LAYERS.forEach(function (layer) {
        var layerBoxes = cardboardBoxes.filter(function (box) { return box.layer === layer; });
        footprints.forEach(function (cup) {
            var boxId = findCupTargetBox(cup, layerBoxes, objectSize);
            if (boxId) {
                hits[layer].assignments[cup.nodeId] = boxId;
            }
            else {
                hits[layer].outside.push(cup.nodeId);
            }
        });
    });
    return hits;
}, { memoizeOptions: { resultEqualityCheck: isEqual } });
var selectCupBoxAssignmentsByLayer = createSelector(selectCupBoxHitsByLayer, function (hits) { return ({
    0: hits[0].assignments,
    1: hits[1].assignments,
}); });
/*
 * Cups that miss the object, per layer. Cardboard derives it from box geometry
 * (above); the shaped objects of the other applications genuinely need the 3D
 * raycast, whose verdicts land in the systemPerformance slice and are
 * single-layer by nature.
 */
var selectCupsOutsideOfObjectByLayer = createSelector(selectCupBoxHitsByLayer, systemPerformanceSelectors.selectRaycastCupsOutsideOfObject, systemDataSelectorsSelectApplication.selectActiveApplication, function (hits, raycastOutside, activeApplication) {
    if (activeApplication !== TactonApplication.CARDBOARD) {
        return { 0: raycastOutside, 1: [] };
    }
    return { 0: hits[0].outside, 1: hits[1].outside };
});
// The active layer's view of the two selectors above — warnings and per-box
// force follow the layer that's currently shown.
var selectCupBoxAssignments = createSelector(selectCupBoxAssignmentsByLayer, uiSelectors.selectActiveLayer, function (byLayer, activeLayer) { return byLayer[activeLayer]; });
var selectCupsOutsideOfObject = createSelector(selectCupsOutsideOfObjectByLayer, uiSelectors.selectActiveLayer, function (byLayer, activeLayer) { return byLayer[activeLayer]; });
/*
 * How many of the active layer's boxes have at least one cup over them.
 * Pure geometry, so it is known before any calculation has run — the
 * dashboard uses it to tell a one-box lift from a multi-box lift while the
 * values are still preliminary, and the application speed readouts use it to
 * turn cycles per minute into boxes per minute.
 */
var selectLiftedBoxCount = createSelector(selectCupBoxAssignments, function (cupBoxAssignments) { return new Set(Object.values(cupBoxAssignments)).size; });
/*
 * Which of the layer's boxes a cup counts toward: its geometric assignment,
 * else the first box — the same fallback as the request serializer, so the
 * client-side aggregation matches what was calculated.
 */
var cupBoxIdResolver = function (boxes, cupBoxAssignments) {
    var _a;
    var defaultBoxId = (_a = boxes[0]) === null || _a === void 0 ? void 0 : _a.id;
    var validBoxIds = new Set(boxes.map(function (box) { return box.id; }));
    return function (cupNodeId) {
        var assigned = cupBoxAssignments[cupNodeId];
        return assigned && validBoxIds.has(assigned) ? assigned : defaultBoxId;
    };
};
/*
 * Per-box lifting force. Each cardboard box is a separate target object;
 * its lifting force is the sum of the forces of the cups currently sitting on
 * it. The API already returns per-cup forces keyed by node id, so this is a
 * client-side aggregation — no extra computation request is needed.
 *
 * Cups marked as outside any box are excluded; cups without an explicit box
 * assignment fall back to the first box (matching the request serializer).
 */
var selectLiftingForcePerBox = createSelector(sceneSelectors.selectCardboardBoxes, uiSelectors.selectActiveLayer, systemPerformanceSelectors.selectActivePerformanceData, selectCupBoxAssignments, selectCupsOutsideOfObject, systemDataSelectorsDefineParameters.selectOutputDefineParameters, function (cardboardBoxes, activeLayer, activePerformanceData, cupBoxAssignments, cupsOutsideOfObject, output) {
    var _a, _b, _c;
    // The displayed performance data + cup assignments are for the active
    // layer, so only aggregate the active layer's boxes.
    var boxes = (cardboardBoxes !== null && cardboardBoxes !== void 0 ? cardboardBoxes : []).filter(function (box) { return box.layer === activeLayer; });
    if (!boxes.length)
        return [];
    var boxIdForCup = cupBoxIdResolver(boxes, cupBoxAssignments);
    var requiredVertical = (_a = output === null || output === void 0 ? void 0 : output.requiredLiftingForceVertical) !== null && _a !== void 0 ? _a : 0;
    var requiredHorizontal = (_b = output === null || output === void 0 ? void 0 : output.requiredLiftingForceHorizontal) !== null && _b !== void 0 ? _b : 0;
    var acc = {};
    boxes.forEach(function (box) {
        acc[box.id] = {
            boxId: box.id,
            verticalAtPS: 0,
            horizontalAtPS: 0,
            verticalAtES: 0,
            horizontalAtES: 0,
            verticalVsTime: [],
            horizontalVsTime: [],
            cupNodeIds: [],
            requiredVertical: requiredVertical,
            requiredHorizontal: requiredHorizontal,
            partSecured: false,
        };
    });
    var addSeries = function (target, source) {
        source.forEach(function (value, i) {
            var _a;
            target[i] = ((_a = target[i]) !== null && _a !== void 0 ? _a : 0) + value;
        });
    };
    var nodeData = (_c = activePerformanceData === null || activePerformanceData === void 0 ? void 0 : activePerformanceData.nodeData) !== null && _c !== void 0 ? _c : {};
    Object.entries(nodeData).forEach(function (_a) {
        var _b = __read(_a, 2), nodeId = _b[0], data = _b[1];
        var cup = data.suctionCup;
        if (!cup)
            return;
        if (cupsOutsideOfObject.includes(nodeId))
            return;
        var box = acc[boxIdForCup(nodeId)];
        if (!box)
            return;
        box.verticalAtPS += cup.liftingForceVerticalAtPS;
        box.horizontalAtPS += cup.liftingForceHorizontalAtPS;
        box.verticalAtES += cup.liftingForceVerticalAtES;
        box.horizontalAtES += cup.liftingForceHorizontalAtES;
        addSeries(box.verticalVsTime, cup.liftingForceVertical);
        addSeries(box.horizontalVsTime, cup.liftingForceHorizontal);
        box.cupNodeIds.push(nodeId);
    });
    return boxes.map(function (box) {
        var entry = acc[box.id];
        entry.partSecured =
            requiredVertical > 0 &&
                requiredHorizontal > 0 &&
                entry.verticalAtPS >= requiredVertical &&
                entry.horizontalAtPS >= requiredHorizontal;
        return entry;
    });
});
/*
 * The boxes actually being lifted (at least one cup on them), each paired
 * with its display number. The number is the 1-based index over ALL of the
 * layer's boxes — the same numbering as the 3D badges and the dashboard's
 * per-box rows — so it stays stable when un-lifted boxes sit in between.
 */
var selectLiftedBoxLiftingForces = createSelector(selectLiftingForcePerBox, function (liftingForcePerBox) {
    return liftingForcePerBox
        .map(function (box, index) { return ({ box: box, boxNumber: index + 1 }); })
        .filter(function (_a) {
        var box = _a.box;
        return box.cupNodeIds.length > 0;
    });
});
/*
 * The lifted box with the smallest margin between its actual and required
 * lifting force at Part Secured — the box that decides whether the whole lift
 * is safe. With several boxes, comparing the sum of all boxes against one
 * box's requirement overstates the margin, so the dashboard headline compares
 * this box instead.
 *
 * The margin is relative (actual / required) so the vertical and horizontal
 * directions are comparable; a box's weaker direction counts. Undefined when
 * fewer than two boxes are lifted (the system-wide totals already tell the
 * whole story then) or while there is no positive requirement to compare
 * against.
 */
var selectWeakestBoxLiftingForce = createSelector(selectLiftedBoxLiftingForces, function (liftedBoxes) {
    if (liftedBoxes.length <= 1)
        return undefined;
    var _a = liftedBoxes[0].box, requiredVertical = _a.requiredVertical, requiredHorizontal = _a.requiredHorizontal;
    if (requiredVertical <= 0 || requiredHorizontal <= 0)
        return undefined;
    var relativeMargin = function (_a) {
        var box = _a.box;
        return Math.min(box.verticalAtPS / requiredVertical, box.horizontalAtPS / requiredHorizontal);
    };
    return liftedBoxes.reduce(function (weakest, candidate) {
        return relativeMargin(candidate) < relativeMargin(weakest) ? candidate : weakest;
    });
});
/*
 * Numbers (as the 3D badges count them) of the lifted boxes with a cup that
 * never reached Part Secured in the latest calculation. The system reaches PS
 * only when every pump and every on-box cup does, and a result that failed
 * that is withheld from the shown data (`revealLayer`), so this reads the
 * latest result — preloaded or shown — directly. Empty unless at least two
 * boxes are lifted: with one box the plain message already says it all.
 */
var selectBoxNumbersNotReachingPartSecured = createSelector(sceneSelectors.selectCardboardBoxes, uiSelectors.selectActiveLayer, systemPerformanceSelectors.selectPreloadedPerformance, systemPerformanceSelectors.selectPerformanceData, selectCupBoxAssignments, selectCupsOutsideOfObject, selectLiftedBoxCount, function (cardboardBoxes, activeLayer, preloadedResult, shownResult, cupBoxAssignments, cupsOutsideOfObject, liftedBoxCount) {
    var result = preloadedResult !== null && preloadedResult !== void 0 ? preloadedResult : shownResult;
    if (liftedBoxCount < 2 || !result)
        return [];
    var boxes = (cardboardBoxes !== null && cardboardBoxes !== void 0 ? cardboardBoxes : []).filter(function (box) { return box.layer === activeLayer; });
    if (!boxes.length)
        return [];
    var boxIdForCup = cupBoxIdResolver(boxes, cupBoxAssignments);
    var failingBoxIds = new Set();
    Object.entries(result.energySavingOff.nodeData).forEach(function (_a) {
        var _b = __read(_a, 2), nodeId = _b[0], data = _b[1];
        var cup = data.suctionCup;
        if (!cup || cup.timeToPartSecured > 0)
            return;
        if (cupsOutsideOfObject.includes(nodeId))
            return;
        failingBoxIds.add(boxIdForCup(nodeId));
    });
    return boxes
        .map(function (box, index) { return (failingBoxIds.has(box.id) ? index + 1 : 0); })
        .filter(function (boxNumber) { return boxNumber > 0; });
});
/*
 * The display number of the lifted box a cup sits on, for the cup's tooltip —
 * only in multi-box mode (two or more lifted boxes), where the number tells
 * the user which badge, breakdown row and graph line the cup belongs to.
 */
var selectLiftedBoxNumberForCup = function (cupNodeId) {
    return createSelector(selectLiftedBoxLiftingForces, selectWeakestBoxLiftingForce, function (liftedBoxes, weakestBox) {
        var _a;
        return weakestBox === undefined
            ? undefined
            : (_a = liftedBoxes.find(function (_a) {
                var box = _a.box;
                return box.cupNodeIds.includes(cupNodeId);
            })) === null || _a === void 0 ? void 0 : _a.boxNumber;
    });
};
/*
 * The lifting forces graph's series and Y domain. In multi-box mode (two or
 * more lifted boxes) the per-box totals are merged in beside the cup lines and
 * the required forces are given so the graph can draw them as reference
 * lines; with one lifted box it is exactly the parsed cup data.
 */
var selectLiftingForcesGraphData = createSelector(systemPerformanceSelectors.selectParsedGraphData, selectLiftedBoxLiftingForces, selectWeakestBoxLiftingForce, function (parsedGraphData, liftedBoxes, weakestBox) {
    if (!parsedGraphData)
        return undefined;
    if (!weakestBox) {
        return __assign(__assign({}, mergeBoxLiftingForcesGraphData(parsedGraphData, [])), { boxes: [] });
    }
    var requiredForces = {
        vertical: weakestBox.box.requiredVertical,
        horizontal: weakestBox.box.requiredHorizontal,
    };
    return __assign(__assign({}, mergeBoxLiftingForcesGraphData(parsedGraphData, liftedBoxes, requiredForces)), { boxes: liftedBoxes, requiredForces: requiredForces });
});
/*
 * Per-box hints from a calculation that failed to reach Part Secured. Such a
 * result is withheld from the shown data (`revealLayer`), which would leave
 * the user with no actual values at all while they rework the system. This
 * reads the withheld result and gives, per lifted box, the peak force its
 * cups produced over the cycle and the vacuum level its weakest cup settled
 * at — enough to tell "add cups" from "fix the vacuum". The weakest box is
 * the one with the smallest peak-to-required margin.
 *
 * Undefined unless the latest result failed PS, the user asked for it with
 * Calculate (`hasSettledUserCalculation` — a good result shows only then, and
 * so does this hint; an edit clears the flag and queues a calculation at
 * once, so a stale hint never describes the current scene) and at least two
 * boxes are lifted — the multi-box case, where the single-object readouts say
 * nothing about any one box.
 */
var selectWithheldBoxLiftingForces = createSelector(sceneSelectors.selectCardboardBoxes, uiSelectors.selectActiveLayer, systemPerformanceSelectors.selectPreloadedPerformance, systemPerformanceSelectors.selectSanityCheckData, systemPerformanceSelectors.selectHasSettledUserCalculation, selectCupBoxAssignments, selectCupsOutsideOfObject, selectLiftedBoxCount, systemDataSelectorsDefineParameters.selectOutputDefineParameters, function (cardboardBoxes, activeLayer, preloadedResult, sanityCheckData, hasSettledUserCalculation, cupBoxAssignments, cupsOutsideOfObject, liftedBoxCount, output) {
    var _a, _b;
    if (!preloadedResult ||
        !sanityCheckData ||
        sanityCheckData.partSecuredReachable ||
        !hasSettledUserCalculation ||
        liftedBoxCount < 2) {
        return undefined;
    }
    var boxes = (cardboardBoxes !== null && cardboardBoxes !== void 0 ? cardboardBoxes : []).filter(function (box) { return box.layer === activeLayer; });
    if (!boxes.length)
        return undefined;
    var requiredVertical = (_a = output === null || output === void 0 ? void 0 : output.requiredLiftingForceVertical) !== null && _a !== void 0 ? _a : 0;
    var requiredHorizontal = (_b = output === null || output === void 0 ? void 0 : output.requiredLiftingForceHorizontal) !== null && _b !== void 0 ? _b : 0;
    if (requiredVertical <= 0 || requiredHorizontal <= 0)
        return undefined;
    var boxIdForCup = cupBoxIdResolver(boxes, cupBoxAssignments);
    var addSeries = function (target, source) {
        source.forEach(function (value, i) {
            var _a;
            target[i] = ((_a = target[i]) !== null && _a !== void 0 ? _a : 0) + value;
        });
    };
    var acc = {};
    boxes.forEach(function (box) {
        acc[box.id] = {
            vertical: [],
            horizontal: [],
            finalVacuumLevel: Number.POSITIVE_INFINITY,
            reachedPartSecured: true,
            cupCount: 0,
        };
    });
    Object.entries(preloadedResult.energySavingOff.nodeData).forEach(function (_a) {
        var _b = __read(_a, 2), nodeId = _b[0], data = _b[1];
        var cup = data.suctionCup;
        if (!cup || cupsOutsideOfObject.includes(nodeId))
            return;
        var entry = acc[boxIdForCup(nodeId)];
        if (!entry)
            return;
        addSeries(entry.vertical, cup.liftingForceVertical);
        addSeries(entry.horizontal, cup.liftingForceHorizontal);
        entry.finalVacuumLevel = Math.min(entry.finalVacuumLevel, cup.finalVacuumLevel);
        entry.reachedPartSecured && (entry.reachedPartSecured = cup.timeToPartSecured > 0);
        entry.cupCount += 1;
    });
    var peak = function (series) {
        return series.length ? Math.max.apply(Math, __spreadArray([], __read(series), false)) : 0;
    };
    var result = [];
    boxes.forEach(function (box, index) {
        var entry = acc[box.id];
        if (!entry.cupCount)
            return;
        var peakVertical = peak(entry.vertical);
        var peakHorizontal = peak(entry.horizontal);
        result.push({
            boxId: box.id,
            boxNumber: index + 1,
            peakVertical: peakVertical,
            peakHorizontal: peakHorizontal,
            requiredVertical: requiredVertical,
            requiredHorizontal: requiredHorizontal,
            peakMeetsRequirement: peakVertical >= requiredVertical &&
                peakHorizontal >= requiredHorizontal,
            finalVacuumLevel: Number.isFinite(entry.finalVacuumLevel)
                ? entry.finalVacuumLevel
                : 0,
            reachedPartSecured: entry.reachedPartSecured,
        });
    });
    if (result.length < 2)
        return undefined;
    var margin = function (box) {
        return Math.min(box.peakVertical / requiredVertical, box.peakHorizontal / requiredHorizontal);
    };
    var weakest = result.reduce(function (weakestSoFar, candidate) {
        return margin(candidate) < margin(weakestSoFar) ? candidate : weakestSoFar;
    });
    return { boxes: result, weakest: weakest };
});
/*
 * Lift status per cardboard box: which of the boxes on the active layer are
 * actually being lifted, and whether their cups hold them. Boxes with no cups
 * are absent from the map.
 *
 * This is the single gate for the "which boxes are lifted" cues — the 3D box
 * tint and the floating force badges both read it, so they can never disagree.
 * Nothing is shown for a single box (the system-wide readouts already cover
 * that case) or while the performance data is stale.
 */
var selectBoxLiftStatuses = createSelector(selectLiftingForcePerBox, systemPerformanceSelectors.selectIsPerformanceDataValid, function (liftingForcePerBox, isPerformanceDataValid) {
    if (!isPerformanceDataValid || liftingForcePerBox.length <= 1)
        return {};
    return liftingForcePerBox.reduce(function (statuses, box) {
        if (box.cupNodeIds.length) {
            statuses[box.boxId] = box.partSecured ? "secured" : "not-secured";
        }
        return statuses;
    }, {});
});
// Ids of cardboard boxes whose centre has been dragged off the pallet. Used to
// flag those boxes with a warning in the 3D scene. Independent of layer/perf
// data — every placed box is checked against the shared pallet footprint.
var selectBoxesOutsidePallet = createSelector(sceneSelectors.selectCardboardBoxes, sceneSelectors.selectPallet, function (cardboardBoxes, pallet) {
    return (cardboardBoxes !== null && cardboardBoxes !== void 0 ? cardboardBoxes : [])
        .filter(function (box) { return !isBoxCentreOnPallet(box.position, pallet, objectPosition); })
        .map(function (box) { return box.id; });
});
/* The layer being worked on, as it would be saved to the user's library. */
var selectActiveLayerPalletLayout = createSelector(sceneSelectors.selectPallet, sceneSelectors.selectObjectSize, sceneSelectors.selectCardboardBoxes, uiSelectors.selectActiveLayer, function (pallet, objectSize, cardboardBoxes, activeLayer) {
    return createPalletLayout({ pallet: pallet, objectSize: objectSize, cardboardBoxes: cardboardBoxes }, activeLayer);
});
/*
 * The saved layer the layer being worked on was laid out from or last saved
 * as — the one saving it again offers to overwrite — if the user still has it.
 */
var selectSavedPalletLayoutInUse = createSelector(userSelectors.selectSavedPalletLayouts, sceneSelectors.selectSavedPalletLayoutIds, uiSelectors.selectActiveLayer, function (savedLayouts, savedLayoutIds, activeLayer) {
    var id = savedLayoutIds === null || savedLayoutIds === void 0 ? void 0 : savedLayoutIds[activeLayer];
    return id === undefined
        ? undefined
        : savedLayouts.find(function (savedLayout) { return savedLayout.id === id; });
});
var combinedSelectors = {
    selectSelectedNode: selectSelectedNode,
    selectFilteredSelectedNodes: selectFilteredSelectedNodes,
    selectSelectedNodes: selectSelectedNodes,
    selectSelectedNodesType: selectSelectedNodesType,
    selectFilteredSelectedNodesType: selectFilteredSelectedNodesType,
    selectSelectedAttachment: selectSelectedAttachment,
    selectSelectedEdge: selectSelectedEdge,
    selectSelectedEdges: selectSelectedEdges,
    selectCanAddTubeConnection: selectCanAddTubeConnection,
    selectCanAddGripPosition: selectCanAddGripPosition,
    selectCanAddCentralizedPump: selectCanAddCentralizedPump,
    selectIsCentralizedPumpLimitReached: selectIsCentralizedPumpLimitReached,
    selectCentralPumpNodesWithoutPump: selectCentralPumpNodesWithoutPump,
    selectVacuumSystemVolume: selectVacuumSystemVolume,
    selectVacuumSystemTubingVolume: selectVacuumSystemTubingVolume,
    selectVacuumSystemComponentVolume: selectVacuumSystemComponentVolume,
    selectIsSystemDataLoading: selectIsSystemDataLoading,
    selectSystemDataErrors: selectSystemDataErrors,
    selectObjectShapes: selectObjectShapes,
    selectSuctionCupModels: selectSuctionCupModels,
    selectUseTexturedSurfaces: selectUseTexturedSurfaces,
    selectCalculatePerformanceEnabled: selectCalculatePerformanceEnabled,
    selectCalculatePerformanceStructuralErrorMessage: selectCalculatePerformanceStructuralErrorMessage,
    selectCalculatePerformanceValidationErrorMessage: selectCalculatePerformanceValidationErrorMessage,
    selectLiftingForcePerBox: selectLiftingForcePerBox,
    selectLiftedBoxLiftingForces: selectLiftedBoxLiftingForces,
    selectWeakestBoxLiftingForce: selectWeakestBoxLiftingForce,
    selectBoxNumbersNotReachingPartSecured: selectBoxNumbersNotReachingPartSecured,
    selectLiftingForcesGraphData: selectLiftingForcesGraphData,
    selectLiftedBoxNumberForCup: selectLiftedBoxNumberForCup,
    selectWithheldBoxLiftingForces: selectWithheldBoxLiftingForces,
    selectBoxLiftStatuses: selectBoxLiftStatuses,
    selectBoxesOutsidePallet: selectBoxesOutsidePallet,
    selectCupBoxAssignments: selectCupBoxAssignments,
    selectCupBoxAssignmentsByLayer: selectCupBoxAssignmentsByLayer,
    selectLiftedBoxCount: selectLiftedBoxCount,
    selectCupsOutsideOfObject: selectCupsOutsideOfObject,
    selectCupsOutsideOfObjectByLayer: selectCupsOutsideOfObjectByLayer,
    selectAvailableGripPositionCount: selectAvailableGripPositionCount,
    selectCupFillLayout: selectCupFillLayout,
    selectActiveLayerPalletLayout: selectActiveLayerPalletLayout,
    selectSavedPalletLayoutInUse: selectSavedPalletLayoutInUse,
    selectFlowRestrictionSummary: selectFlowRestrictionSummary,
};
export var selectors = __assign(__assign(__assign(__assign(__assign(__assign(__assign(__assign({}, combinedSelectors), uiSelectors), sceneSelectors), systemDataSelectorsDefineParameters), systemDataSelectorsSelectApplication), systemPerformanceSelectors), userSelectors), undoSnapshotsSelectors);
